import dayjs from 'dayjs';
import { FilterService } from 'primereact/api';
import { Badge } from 'primereact/badge';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { Column, ColumnFilterElementTemplateOptions } from 'primereact/column';
import { DataTable, DataTableFilterMeta, DataTableSortMeta } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { EditToken, Token } from './EditToken';
import { PartnerService, Partner } from '../services/PartnerService';
import './TokenList.css';
import { TokenService } from '../services/TokenService';
import { AxiosError } from 'axios';
import { saveAs } from 'file-saver';
import { useAuth } from '../contexts/AuthContext';
import Fragment from './Fragment';
import { useFragment } from '../contexts/MessageTemplateContext';

const getStatusClassName = (status: string) => {
    switch (status.toLowerCase()) {
        case 'created':
            return 'status-created';
        case 'sent':
            return 'status-sent';
        case 'filled':
            return 'status-filled';
        case 'exported':
            return 'status-exported';
        default:
            return '';
    }
};

const customStatusFilter = (value: string, filter: any): boolean => {
    if (!value || !filter || filter.length === 0) {
        return true;
    }
    return filter.includes(value);
};

FilterService.register('custom_status', customStatusFilter);

const TokenList: React.FC = () => {
    const { t } = useTranslation();
    const toast = useRef<Toast>(null);
    const { isAdmin } = useAuth();
    const [searchText, setSearchText] = useState('');
    const [open, setOpen] = useState(false);
    const [selectedToken, setSelectedToken] = useState<Partial<Token>>({});
    const [selectionModel, setSelectionModel] = useState<Partial<Token>[]>([]);
    const [globalFilter, setGlobalFilter] = useState<string | null>(null);
    const [multiSortMeta, setMultiSortMeta] = useState<DataTableSortMeta[] | null | undefined>([
        { field: 'createdAt', order: -1 },
        { field: 'name', order: 1 }
    ]);
    const oneMonthAgo = dayjs().subtract(1, 'month').toDate();

    const [filters, setFilters] = useState<DataTableFilterMeta>({
        createdAt: { value: oneMonthAgo, matchMode: 'dateAfter' },
        exportedAt: { value: null, matchMode: 'dateAfter' },
        filledAt: { value: null, matchMode: 'dateAfter' },
        name: { value: null, matchMode: 'contains' },
        companyname: { value: null, matchMode: 'contains' },
        status: { value: null, matchMode: 'custom' }
    });

    const [tokens, setTokens] = useState<Token[]>([]);
    const [partners, setPartners] = useState<Partner[]>([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [sendingEmail, setSendingEmail] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const pleaseFillFragment = useFragment('pleasefill');
    const [confirmEmailOpen, setConfirmEmailOpen] = useState(false);
    const [pendingEmailTokens, setPendingEmailTokens] = useState<Partial<Token>[]>([]);
    const [partnerDialogOpen, setPartnerDialogOpen] = useState(false);

    const handleAxiosError = (error: any, summary: string) => {
        let message = t('tokenList.unknownError');
        if (error.isAxiosError) {
            const axiosError = error as AxiosError;
            message = axiosError.message;
        } else {
            console.error(summary, error);
        }
        toast.current?.show({ severity: 'error', summary, detail: message, life: 3000 });
    };

    const fetchTokens = async () => {
        setLoading(true);
        try {
            const data = await TokenService.fetchTokens();
            setTokens(data);
        } catch (error: any) {
            handleAxiosError(error, t('tokenList.fetchError'));
        } finally {
           setLoading(false);
        }
    };

    const fetchPartners = async () => {
        try {
            const data = await PartnerService.fetchPartners();
            // only partners with tokens can be picked
            const withTokens = data.filter(p => (p.numberOfTokens ?? 0) > 0);
            setPartners(withTokens);
        } catch (error: any) {
            console.error('Failed to fetch partners', error);
            setPartners([]);
        }
    };

    useEffect(() => {
        fetchTokens();
        fetchPartners();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const statusOptions = [
        { label: t('tokenStatus.created'), value: 'created' },
        { label: t('tokenStatus.sent'), value: 'sent' },
        { label: t('tokenStatus.filled'), value: 'filled' },
        { label: t('tokenStatus.exported'), value: 'exported' }
    ];

    const statusFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
        return (
            <div className="p-column-filter">
                {statusOptions.map((statusOption) => (
                    <div key={statusOption.value} className="field-checkbox">
                        <Checkbox
                            inputId={statusOption.value}
                            value={statusOption.value}
                            onChange={(e) => {
                                const selectedValues = options.value || [];
                                if (e.checked) {
                                    options.filterCallback([...selectedValues, e.value]);
                                } else {
                                    options.filterCallback(selectedValues.filter((val: string) => val !== e.value));
                                }
                            }}
                            checked={options.value?.includes(statusOption.value)}
                        />
                        <label htmlFor={statusOption.value} className={`ml-2 status-label ${getStatusClassName(statusOption.value)}`}>{statusOption.label}</label>
                    </div>
                ))}
            </div>
        );
    };

    const handleOpen = (token: Partial<Token>) => {
        setSelectedToken(token);
        setOpen(true);
    };

    const handleClose = () => {
        fetchTokens();
        setOpen(false);
    };

    const handleSave = async (token: Token) => {
        try {
            if (token.id) {
                await TokenService.editToken(token.id, token);
            } else {
                await TokenService.createToken(token);
            }
            fetchTokens();
            fetchPartners();
            setOpen(false);
        } catch (error: any) {
            handleAxiosError(error, t('editToken.saveError'));
        }
    };

    const handleAdd = () => {
        if (isAdmin) {
            setSelectedToken({
                sendWelcomeEmail: true
            });
            setOpen(true);
            return;
        }
        // for non-admins: if there's exactly one partner available, skip selector
        if (partners.length === 1) {
            const only = partners[0];
            setSelectedToken({ partnerId: only.id, partnerName: only?.name, sendWelcomeEmail: true });
            setOpen(true);
            return;
        }
        setPartnerDialogOpen(true);
    };

    const handlePartnerNext = (partner: Partner) => {
        setPartnerDialogOpen(false);
        if (!partner) return;

        setSelectedToken({ partnerId: partner.id, partnerName: partner?.name, sendWelcomeEmail: true });
        setOpen(true);
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const selectedIds: string[] = selectionModel.map(token => token.id!);
            const response = await TokenService.exportTokens(selectedIds);
            const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            saveAs(blob, `tokens_export_${dayjs().format('YYYYMMDD')}.xlsx`);
            fetchTokens();
        } catch (error: any) {
            handleAxiosError(error, t('tokenList.exportError'));
        } finally {
            setExporting(false);
        }
    };
    
    const handleRowExport = async (token: Partial<Token>) => {
        setExporting(true);
        try {
            const response = await TokenService.exportTokens([token.id!]);
            const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            saveAs(blob, `tokens_export_${dayjs().format('YYYYMMDD')}.xlsx`);
            fetchTokens();
        } catch (error: any) {
            handleAxiosError(error, t('tokenList.exportError'));
        } finally {
            setExporting(false);
        }
    };

    const handleRowDownload = async (token: Partial<Token>) => {
        if (!token.id) return;
        setDownloading(true);
        try {
            const blob = await TokenService.downloadResultFile(token.id);
            const filename = `${token.name || 'result'}.pdf`;
            saveAs(blob, filename);
        } catch (error: any) {
            handleAxiosError(error, t('tokenList.downloadError'));
        } finally {
            setDownloading(false);
        }
    };

    // Original email sending logic extracted so confirm dialog can call them
    const handleSendEmail = async () => {
        setSendingEmail(true);
        try {
            const selectedIds: string[] = selectionModel.map(token => token.id!);
            await TokenService.sendEmails(selectedIds);
            toast.current?.show({ severity: 'success', summary: t('tokenList.emailSuccess'), life: 3000 });
        } catch (error: any) {
            handleAxiosError(error, t('tokenList.emailError'));
        } finally {
            setSendingEmail(false);
        }
    };

    const handleRowSendEmail = async (token: Partial<Token>) => {
        setSendingEmail(true);
        try {
            await TokenService.sendEmails([token.id!]);
            toast.current?.show({ severity: 'success', summary: t('tokenList.emailSuccess'), life: 3000 });
        } catch (error: any) {
            handleAxiosError(error, t('tokenList.emailError'));
        } finally {
            setSendingEmail(false);
        }
    };

    const openEmailConfirm = (tokensToSend: Partial<Token>[]) => {
        setPendingEmailTokens(tokensToSend);
        setConfirmEmailOpen(true);
    };

    const confirmSendEmails = async () => {
        setConfirmEmailOpen(false);
        if (pendingEmailTokens.length === 0) return;
        if (pendingEmailTokens.length === 1) {
            await handleRowSendEmail(pendingEmailTokens[0]);
        } else {
            await handleSendEmail();
        }
    };

    const cancelSendEmails = () => {
        setPendingEmailTokens([]);
        setConfirmEmailOpen(false);
    };

    const handleSendEmailWithConfirm = () => {
        if (selectionModel.length === 0) return;
        openEmailConfirm(selectionModel);
    };

    const handleRowSendEmailWithConfirm = (token: Partial<Token>) => {
        openEmailConfirm([token]);
    };

    // Email preview building using first token (if multiple selected show generic info)
    const previewToken = pendingEmailTokens[0];
    const replacements: Record<string, any> = { ...(previewToken || {}) };

    const formatDate = (date?: Date) => date ? dayjs(date).format('L') : '-';

    const handleSearch = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(event.target.value);
        setGlobalFilter(event.target.value);
    }, [setSearchText, setGlobalFilter]);

    const renderHeader = () => {
        return (
            <div className="flex justify-content-between">
                <IconField iconPosition="right">
                    <InputText value={searchText} onChange={handleSearch} placeholder={t('tokenList.searchLabel')} />
                    <InputIcon className="pi pi-search" />
                </IconField>
                <div className="flex align-items-center">
                    {selectionModel.length > 0 && (
                        <>
                            { isAdmin && <Button
                                label={t('tokenList.export')}
                                icon="pi pi-upload" outlined
                                onClick={handleExport}
                                className="ml-2"
                            />}
                            <Button
                                label={t('editToken.emailButton')}
                                icon="pi pi-envelope" outlined
                                onClick={handleSendEmailWithConfirm}
                                className="ml-2"
                            />
                        </>
                    )}
                </div>
            </div>
        );
    };

    const dateFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
        return <Calendar value={options.value} onChange={(e) => options.filterCallback(e.value, options.index)} />;
    };

    return (
        <div className="p-grid p-justify-center">
            <Toast ref={toast} />
            {(exporting || sendingEmail || downloading) && (
                <div className="export-overlay">
                    <ProgressSpinner />
                </div>
            )}
            <div className="p-col-12 p-md-10">
                {loading ? (
                    <div className="flex justify-content-center align-items-center" style={{ height: 'calc(100vh - 200px)' }}>
                        <ProgressSpinner />
                    </div>
                ) : (
                    <DataTable
                        size='small'
                        value={tokens}
                        selection={selectionModel}
                        onSelectionChange={(e) => { console.log(e.value); setSelectionModel(e.value) }}
                        dataKey="id"
                        scrollable
                        scrollHeight='calc(100vh - 300px)'
                        selectionMode="checkbox"
                        globalFilter={globalFilter}
                        header={renderHeader()}
                        sortMode="multiple"
                        multiSortMeta={multiSortMeta}
                        onSort={(e) => setMultiSortMeta(e.multiSortMeta)}
                        filters={filters}
                        resizableColumns
                        onFilter={(e) => setFilters(e.filters)}
                    >
                        <Column selectionMode="multiple" headerStyle={{ width: '3rem' }}></Column>
                        <Column field="partnerName" header={t('tokenList.partnerName')} sortable />
                        <Column field="name" header={t('tokenList.name')} sortable />
                        <Column
                            field="status"
                            header={t('tokenList.status')}
                            body={(rowData: Token) => {
                                const status = rowData.status;
                                return (
                                    <span className={`status-label ${getStatusClassName(status)}`}>
                                        {t(`tokenStatus.${status.toLowerCase()}`)}
                                    </span>
                                );
                            }}
                            filter
                            showFilterMenuOptions={false}
                            onFilterApplyClick={(e) => console.log('onFilterApplyClick', e)}
                            filterElement={statusFilterTemplate}
                        />
                        <Column field="createdAt" header={t('tokenList.createdAt')} body={(rowData: Partial<Token>) => formatDate(rowData.createdAt)} sortable dataType="date" filter filterElement={dateFilterTemplate} />
                        <Column field="filledAt" header={t('tokenList.filledAt')} body={(rowData: Partial<Token>) => formatDate(rowData.filledAt)} sortable dataType="date" filter filterElement={dateFilterTemplate} />
                        <Column
                            field="actions"
                            header={t('tokenList.actions')}
                            body={(rowData: Partial<Token>) => (
                                <div className="action-buttons" style={{ whiteSpace: 'nowrap' }}>
                                    <span className="pi pi-pencil mr-3" onClick={() => handleOpen(rowData)}></span>
                                    {rowData.fileUploaded && (
                                        <span className="pi pi-download mr-3" onClick={() => handleRowDownload(rowData)}></span>
                                    )}
                                    {isAdmin && <span className="pi pi-upload mr-3" onClick={() => handleRowExport(rowData)}></span>}
                                    <span className="pi pi-envelope" onClick={() => handleRowSendEmailWithConfirm(rowData)}></span>
                                </div>
                            )}
                        />
                    </DataTable>
                )}
                { (isAdmin || partners.length > 0) && (
                    <div style={{ position: 'fixed', bottom: 16, right: 16 }}>
                        <Button
                            icon="pi pi-plus"
                            className="p-button-rounded p-button-primary"
                            onClick={handleAdd}
                        />
                        {!isAdmin && partners.length > 0 && (
                            <Badge 
                                value={partners.reduce((sum, p) => sum + (p.numberOfTokens || 0), 0)} 
                                severity="success"
                                style={{ position: 'absolute', top: -8, right: -8 }}
                            />
                        )}
                    </div>
                )}
                <Dialog header={t('tokenList.selectPartnerHeader')} visible={partnerDialogOpen} style={{ width: '30vw', minWidth: 300 }} onHide={() => setPartnerDialogOpen(false)}>
                    <div className="p-fluid">
                        <label>{t('tokenList.selectPartner')}</label>
                        <div style={{ marginTop: 8 }}>
                            {partners.map(p => (
                                <div key={p.id} style={{ marginBottom: 8 }}>
                                    <Button style={{ width: '100%' }} className="p-button-outlined" label={p.name} onClick={() => handlePartnerNext(p)} />
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="p-d-flex p-jc-end p-mt-3">
                        <Button label={t('common.cancel', 'Cancel')} className="p-button-text" onClick={() => setPartnerDialogOpen(false)} />
                    </div>
                </Dialog>
                <Dialog header={t('editToken.title')} visible={open} dismissableMask={true} style={{ width: '90vw' }} onHide={handleClose}>
                    <EditToken token={selectedToken} onSave={handleSave} />
                </Dialog>
                <Dialog
                    header={t('tokenList.confirmEmailHeader')}
                    visible={confirmEmailOpen}
                    style={{ width: '60vw', maxWidth: 800 }}
                    modal
                    onHide={cancelSendEmails}
                    footer={<div className="flex justify-content-end gap-2">
                        <Button label={t('common.cancel', 'Cancel')} icon="pi pi-times" className="p-button-text" onClick={cancelSendEmails} />
                        <Button label={t('common.send', 'Send')} icon="pi pi-send" onClick={confirmSendEmails} autoFocus />
                    </div>}
                >
                    <div className="mb-3">
                        {pendingEmailTokens.length > 1 ? (
                            <p>{t('tokenList.confirmEmailMultiple', { count: pendingEmailTokens.length })}</p>
                        ) : (
                            <p>{t('tokenList.confirmEmailSingle')}</p>
                        )}
                    </div>
                    {pleaseFillFragment && (
                        <div className="fragment-preview p-3" style={{ border: '1px solid var(--surface-border)', borderRadius: 4, maxHeight: 300, overflow: 'auto' }}>
                            <h4 style={{ marginTop: 0 }}>{pleaseFillFragment.subject || t('tokenList.emailPreview')}</h4>
                            <Fragment text={pleaseFillFragment.content} replacements={replacements} />
                        </div>
                    )}
                    {!pleaseFillFragment && <small>{t('tokenList.fragmentMissing')}</small>}
                </Dialog>
            </div>
        </div>
    );
};

export default TokenList;